'use client';

import * as React from 'react';
import {useInView} from 'react-intersection-observer';
import {ImageWithFallback} from '@purpose/ui/components/ImageWithFallback';
import {cn} from '@purpose/ui/lib/utils';
import Image1 from '@purpose/ui/assets/static/cardImages/card-placeholder-1.svg';
import Image2 from '@purpose/ui/assets/static/cardImages/card-placeholder-2.svg';
import Image3 from '@purpose/ui/assets/static/cardImages/card-placeholder-3.svg';
import Image4 from '@purpose/ui/assets/static/cardImages/card-placeholder-4.svg';
import Image5 from '@purpose/ui/assets/static/cardImages/card-placeholder-5.svg';
import {Skeleton} from '../skeleton';
import {Typography} from '../typography';
import {Button} from '../button';
import {useResponsive} from '@purpose/ui/hooks';

interface ContentCardProps {
  Icon?: any;
  withTitle?: boolean;
  withSubtitle?: boolean;
  description?: string;
  buttonProps?: {
    title: string;
    Icon?: any;
    onClick: () => void;
  };
  priority?: boolean;
}

const ContentPlaceholderCard = React.forwardRef<
  HTMLDivElement,
  ContentCardProps
>(
  (
    {
      Icon = null,
      withTitle,
      withSubtitle,
      description,
      buttonProps,
      priority = false
    },
    ref
  ) => {
    const {isTabletOrSmaller} = useResponsive();

    // Use intersection observer to determine if card is in view
    const {ref: inViewRef, inView} = useInView({
      threshold: 0.1,
      triggerOnce: true
    });

    // Combine refs
    const combinedRef = React.useMemo(() => {
      return (node: HTMLDivElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        inViewRef(node);
      };
    }, [ref, inViewRef]);

    const randomBg = React.useMemo(() => {
      const images = [Image1, Image2, Image3, Image4, Image5];
      const randomElement = images[Math.floor(Math.random() * images.length)];
      return randomElement;
    }, []);

    return (
      <div
        ref={combinedRef}
        className={cn(
          'bg-card relative flex w-full flex-col gap-1 overflow-hidden rounded-lg',
          'cursor-default transition-shadow hover:shadow-md'
        )}
      >
        <div className="card-container relative aspect-4/3 overflow-hidden rounded-t-lg">
          <div className="absolute z-10 flex h-full w-full flex-col items-center justify-center gap-2 px-3 md:gap-3 md:px-9">
            {Icon}
            {description && (
              <Typography
                variant={isTabletOrSmaller || !buttonProps ? 'body1' : 'body2'}
                className="text-secondary-95 text-center"
              >
                {description}
              </Typography>
            )}
            {buttonProps && (
              <Button
                variant="secondary"
                className="bg-secondary-0 flex h-7! flex-row items-center gap-1 px-1 md:gap-2 md:px-2"
                onClick={buttonProps.onClick}
              >
                {buttonProps?.Icon}
                <Typography variant={isTabletOrSmaller ? 'body1' : 'body2'}>
                  {buttonProps?.title}
                </Typography>
              </Button>
            )}
          </div>
          <ImageWithFallback
            src={randomBg}
            fill
            className="card-image h-full w-full object-cover"
            priority={priority || inView}
            loading={priority || inView ? 'eager' : 'lazy'}
            placeholder="empty"
            quality={80}
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw"
            iconStyles={{
              width: 64,
              height: 64
            }}
          />
        </div>
        <div className="flex flex-col gap-4 p-2 md:gap-6">
          {withTitle && (
            <Skeleton.Block
              className="animate-none"
              height="10px"
              width="70%"
            />
          )}
          {withSubtitle && (
            <Skeleton.Block className="animate-none" height="8px" width="44%" />
          )}
        </div>
      </div>
    );
  }
);

ContentPlaceholderCard.displayName = 'ContentCard';

export {ContentPlaceholderCard};
export type {ContentCardProps};
